import { isFunction, isUndefined, has, defer, isEmpty, extend } from 'underscore';
import React from 'react';
import PropTypes from 'prop-types';
import Promise from 'bluebird';

import { getToolbarPlugin } from '@bingads-webui-campaign-react/toolbar-plugin';
import { ToolbarContainer } from '@bingads-webui-campaign-react/toolbar-container';
import { getCrossPageSelectPlugin } from '@bingads-webui-campaign-react/cross-page-select-plugin';
/* global Map */
// Ad-hoc synchronization
class Latch {
  constructor() {
    this.onInitialized = new Promise((resolve) => {
      this.resolveOnInitialized = () => {
        this.isInitialized = true;
        resolve();
      };
    });
    this.isInitialized = false;
    this.isScheduled = {};
  }

  release() {
    return this.resolveOnInitialized();
  }

  schedule(callback) {
    if (this.isInitialized) return callback();
    if (has(this.isScheduled, callback)) return null;

    this.isScheduled[callback] = true;

    return this.onInitialized.then(() => defer(callback));
  }
}

export const withTablePageStateManager = ({ getToolbarConfig }) =>
  (WrappedComponent) => {
    class GridPageStateManager extends React.PureComponent {
      static propTypes = {
        permissions: PropTypes.shape({}).isRequired,
        scope: PropTypes.shape({
          values: PropTypes.shape({}),
          location: PropTypes.shape({
            pathname: PropTypes.string,
          }),
        }).isRequired,
        siteMap: PropTypes.arrayOf(PropTypes.shape({
          id: PropTypes.string,
        })).isRequired,
        i18n: PropTypes.shape({
          getString: PropTypes.func,
        }).isRequired,
      };

      constructor(props) {
        super(props);
        this.initial(props);
        this.unmounted = false;
      }

      componentWillUnmount() {
        this.unmounted = true;
      }

      onGridInjected = ({ deselectAll, ...rest }) => {
        this.deselectAll = deselectAll;
        this.lazyInjected = rest;

        this.lazyInjected.backboneGridView.once('willReload', () => {
          this.onInitialized.release();
        });
      };

      onClearSelection = () => {
        this.setState({
          showToolbarSelected: false,
          showToolbarUnselected: true,
        });

        if (isFunction(this.deselectAll)) {
          this.deselectAll();
        }
      };

      initial(props) {
        const { scope } = props;
        const crossPageSelectPlugin = getCrossPageSelectPlugin({
          updateGridSelectionState: this.updateGridSelectionState,
        });
        let isOpenCreatePanel = false;
        isOpenCreatePanel = !!scope.values.isOpenCreatePanel;
        if (isOpenCreatePanel) {
          delete scope.values.isOpenCreatePanel;
          scope.history.replace({
            pathname: scope.history.location.pathname,
            search: scope.stringify(),
          });
        }
        const { onSelectCrossPage } = crossPageSelectPlugin;
        const { toolbarSelectedConfig, toolbarUnselectedConfig, toolbarDynamicSelectedConfigCallback } = getToolbarConfig({
          ...props,
          isOpenCreatePanel,
          updateGridOptions: this.updateGridOptions.bind(this),
          onSelectCrossPage,
          showTopOfGridPanel: this.showTopOfGridPanel,
          lazyInject: () => this.lazyInjected, // call back to get lazy created dependencies, such as backboneGridView, filterService
        });

        this.crossPageSelectPlugin = crossPageSelectPlugin;
        this.toolbarSelectedConfig = toolbarSelectedConfig;
        this.toolbarUnselectedConfig = toolbarUnselectedConfig;
        this.toolbarDynamicSelectedConfigCallback = toolbarDynamicSelectedConfigCallback;
        this.toolbarDynamicSelectedConfig = new Map();
        this.initialSetGridNum = true;
        if (!this.state) {
          this.state = {
            isOpenCreatePanel,
            forceHideInlineChart: false,
            forceHideToolbar: false,
            panelView: null,
            showInlineChart: true,
            showToolbarSelected: false,
            showToolbarUnselected: true,
            showTopGridPanel: false,
            toolbarSelectedConfig: this.toolbarSelectedConfig,
            toolbarUnselectedConfig: this.toolbarUnselectedConfig,
            forceResetScrollFlag: false,
            selectionCount: 0,
            totalCount: 0,
            isMultiPage: false,
            isSinglePageAllSelected: false,
            isCrossPageAllSelected: false,
          };
        } else {
          this.setState({
            toolbarSelectedConfig: this.toolbarSelectedConfig,
            toolbarUnselectedConfig: this.toolbarUnselectedConfig,
          });
        }
        this.createToolbar();
      }

      updateGridOptions(gridOptions) {
        this.setState({
          gridOptions,
        });
      }

      createToolbar() {
        this.toolbarPlugin = getToolbarPlugin({
          overrideWith: this.showTopOfGridPanel,
          resetVisibility: ({ forceHideToolbar }) => {
            this.setState({
              forceHideToolbar,
              showTopGridPanel: forceHideToolbar,
            });
          },
          resetGraphVisibility: () => {
            this.setState({
              forceHideInlineChart: false,
            });
          },
          resetToolbarMode: ({ showToolbarSelected, showToolbarUnselected }) => {
            this.setState({
              showToolbarSelected,
              showToolbarUnselected,
            });
          },
          toolbarConfig: {
            toolbarSelectedConfig: this.state.toolbarSelectedConfig,
            // this.toolbarDynamicSelectedConfigCallback can mute so trigger it as lambda expression from here to ensure newest function is always used
            toolbarDynamicSelectedConfigCallback: isFunction(this.toolbarDynamicSelectedConfigCallback) && (params => this.toolbarDynamicSelectedConfigCallback(params)),
            toolbarUnselectedConfig: this.state.toolbarUnselectedConfig,
          },
          updateToolbarDynamicSelectedConfig: ({ dynamicSelectedConfig }) => {
            this.toolbarDynamicSelectedConfig = dynamicSelectedConfig;
            this.updateToolbar();
          },
          updateToolbar: this.updateToolbar,
        });

        this.onInitialized = new Latch();
      }

      updateToolbar = () => {
        const doUpdate = () => {
          if (this.unmounted) {
            return;
          }

          // Merge the selected config with the dynamic selected config. Map.set() adds or updates an element so it will
          // respect the order of elements if the key existed previously from selected config, otherwise add it to the end.
          const toolbarSelectedConfigMerged = new Map();
          this.toolbarSelectedConfig.forEach((value, key) => toolbarSelectedConfigMerged.set(key, value));
          this.toolbarDynamicSelectedConfig.forEach((dynamicValue, key) => {
            const mergedValue = extend({}, toolbarSelectedConfigMerged.get(key), dynamicValue);
            toolbarSelectedConfigMerged.set(key, mergedValue);
          });

          const toolbarSelectedConfig = new Map();
          const toolbarUnselectedConfig = new Map();
          toolbarSelectedConfigMerged.forEach((value, key) => {
            if (!isEmpty(value)) {
              toolbarSelectedConfig.set(key, value);
            }
          });
          this.toolbarUnselectedConfig.forEach((value, key) => toolbarUnselectedConfig.set(key, value));
          this.setState({
            toolbarSelectedConfig,
            toolbarUnselectedConfig,
          });
        };

        this.onInitialized.schedule(doUpdate);
      };

      resetToolbarVisibility = ({ forceHideToolbar }) => {
        this.setState({ forceHideToolbar });
      };

      showTopOfGridPanel = (viewInstance, config = {}) => {
        if (viewInstance) {
          this.setState(prevState => ({
            panelView: viewInstance,
            showTopGridPanel: true,
            forceHideInlineChart: true,
            forceHideToolbar: config.forceHideToolbar !== undefined ? config.forceHideToolbar : true,
            forceResetScrollFlag: config.forceResetScrollFlag !== undefined ? config.forceResetScrollFlag : !prevState.forceResetScrollFlag,
            showTopGridPanelAndContent: config.showTopGridPanelAndContent !== undefined ? config.showTopGridPanelAndContent : false,
          }));
        } else {
          this.setState(prevState => ({
            panelView: null,
            showTopGridPanel: false,
            forceHideInlineChart: false,
            forceHideToolbar: false,
            forceResetScrollFlag: config.forceResetScrollFlag !== undefined ? config.forceResetScrollFlag : !prevState.forceResetScrollFlag,
            showTopGridPanelAndContent: false,
          }));
        }
      };

      showInlineChartView = () => {
        this.setState({ showInlineChart: true });
      };

      updateGridSelectionState = ({
        selectionCount,
        totalCount,
        isMultiPage,
        isSinglePageAllSelected,
        isCrossPageAllSelected,
      }) => {
        const doUpdate = () => {
          if (this.unmounted) {
            return;
          }

          // for performance consideration, we do not need to update totalCount if no rows is selected
          const newTotal = selectionCount === 0 && this.initialSetGridNum ? 0 : totalCount;
          if (selectionCount || totalCount) {
            this.initialSetGridNum = false;
          }
          this.setState({
            selectionCount,
            totalCount: newTotal,
            isMultiPage,
            isSinglePageAllSelected,
            isCrossPageAllSelected,
          });
        };

        this.onInitialized.schedule(doUpdate);
      };

      updateSelectedToolbarConfig = (newSelectedConfig) => {
        this.toolbarSelectedConfig = newSelectedConfig;
        this.updateToolbar();
      };

      updateUnselectedToolbarConfig = (newUnselectedConfig) => {
        this.toolbarUnselectedConfig = newUnselectedConfig;
        this.updateToolbar();
      };

      /**
       * This will force all the config toolbar (selected/unselected/dynamicSelectedCallback) to get updated by
       * passing the page newest props in parameter of the getToolbarConfig function and reload current configuration.
       * @param {object} newProps It can also accept some object to merge to current props so getToolbarConfig(context) is more rich
       * @return {void}
       */
      forceInitToolbarConfig = (newProps = {}) => {
        this.initial({ ...this.props, ...newProps });
        this.updateToolbar();
      };

      renderToolbarContainer = (options = {}) => {
        const {
          forceHideToolbar,
          isCrossPageAllSelected,
          isMultiPage,
          isSinglePageAllSelected,
          selectionCount,
          showToolbarSelected,
          showToolbarUnselected,
          toolbarSelectedConfig,
          toolbarUnselectedConfig,
          totalCount,
        } = this.state;

        return (
          <ToolbarContainer
            forceHideToolbar={forceHideToolbar}
            i18n={this.props.i18n}
            isCrossPageAllSelected={isCrossPageAllSelected}
            isMultiPage={isUndefined(options.isMultiPage) ? isMultiPage : options.isMultiPage}
            isSinglePageAllSelected={isSinglePageAllSelected}
            onClear={this.onClearSelection}
            selectionCount={selectionCount}
            showToolbarSelected={showToolbarSelected}
            showToolbarUnselected={showToolbarUnselected}
            toolbarSelectedConfig={toolbarSelectedConfig}
            toolbarUnselectedConfig={toolbarUnselectedConfig}
            totalCount={totalCount}
          />
        );
      };

      render() {
        const {
          showInlineChart,
          forceHideInlineChart,
          showTopGridPanel,
          showTopGridPanelAndContent,
        } = this.state;

        return (
          <WrappedComponent
            {...this.props}
            {...this.state}
            toolbarSelectedConfig={this.state.toolbarSelectedConfig}
            toolbarUnselectedConfig={this.state.toolbarUnselectedConfig}
            crossPageSelectPlugin={this.crossPageSelectPlugin}
            toolbarPlugin={this.toolbarPlugin}
            showInlineChart={showInlineChart && !forceHideInlineChart}
            showTopGridPanel={showTopGridPanel}
            showTopGridPanelAndContent={showTopGridPanelAndContent}
            renderToolbarContainer={this.renderToolbarContainer}
            showTopOfGridPanel={this.showTopOfGridPanel}
            onGridInjected={this.onGridInjected}
            updateToolbar={this.updateToolbar}
            resetToolbarVisibility={this.resetToolbarVisibility}
            updateSelectedToolbarConfig={this.updateSelectedToolbarConfig}
            updateUnselectedToolbarConfig={this.updateUnselectedToolbarConfig}
            forceInitToolbarConfig={this.forceInitToolbarConfig}
          />
        );
      }
    }

    return GridPageStateManager;
  };
